/* eslint-disable react/jsx-props-no-spreading */
import * as ReactDOMClient from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type FC, useCallback, useContext, useEffect, useRef } from 'react';
import { useSearchBlockQuery, type SearchBlockQuery } from 'cms/cms';
import { type SearchCount, resolveSearchBlock } from 'cms/resolvers/searchBlockResolver';
import SearchBlockProvider from './SearchBlockProvider';
import SearchBlockContext, { type SearchBlockContextProps, type SearchBlockProps } from './SearchBlockContext';
import { getQueryURL, getFilterQuery, getActiveFilterSingleValue, getUpdatedFilters, getPageQueryURL, getAllActiveFilterValueLabels } from '3-components/ListingApp/ListingFilters/ListingFilterHelpers';
import { DEFAULT_FETCH_PARAMS, OPTMIZELY_GRAPHQL_BASE_URL } from 'cms/constants';
// import ListingHeader from '3-components/ListingApp/ListingHeader/ListingHeader';
import ListingContent from '3-components/ListingApp/ListingContent/ListingContent';
import { ListingError, ListingLoader } from '3-components/ListingApp/ListingUI/ListingUI';
import { getSearchWhereValue } from './SearchBlockHelpers';
import SearchItem from './SearchItem';
import RadioTab from '3-components/RadioTab/RadioTab';
import RadioTabItem from '3-components/RadioTab/RadioTabItem';
import ListingFilterInput from '3-components/ListingApp/ListingFilters/ListingFilterInput';
import type { IconString } from '2-core-elements/Icon/Icon';
import { type ActiveFilter } from '3-components/ListingApp/ListingFilters/ListingFiltersTypes';
import { type Locales, Ranking, OrderBy } from 'cms/cms-types';
import { removeBrandPrefixFromLabel } from 'utils/string';


const getTabIconFromValue = (value: string): IconString => {
    switch (value) {
    case 'PersonPage': return 'person';
    case 'AssetPage': return 'star';
    case 'LocationPage': return 'star';
    case 'ArticlePage': return 'calendar';
    default: return 'menu';
    }
};

const getTabSearchCount = (value: string, searchCounts: SearchCount[]): string => {
    // for All results tab count
    if (!value) {
        return searchCounts.find(({ name }) => {
            return name === 'PageBase';
        })?.count.toString() || '';
    }

    return searchCounts.find(({ name }) => {
        return name === value;
    })?.count.toString() || '';
};

const getAncestorTabSearchCount = (value: string, ancestorCounts: SearchCount[], allValue?: string): string => {
    // for All results tab count
    if (!value) {
        if (allValue) {
            return ancestorCounts.find(({ name }) => {
                return name === allValue;
            })?.count.toString() || '';
        }
        return ancestorCounts[0]?.count.toString() || '';
    }

    return ancestorCounts.find(({ name }) => {
        return name === value;
    })?.count.toString() || '';
};

const SearchSection: FC = () => {
    const {
        locale,
        activeFilters,
        setActiveFilters,
        listing: {
            filters,
            itemsPerPage = 10,
            tagsLabel,
            ancestorsTagLabel,
            ancestorLimit,
            firstAncestor,
            // filterByAncestors,
        },
        page,
        setPage,
        label,
        cms,
    } = useContext(SearchBlockContext) as SearchBlockContextProps;

    const { search, ancestors, ...restParams } = getFilterQuery(activeFilters, filters);

    const scrollToEl = useRef<HTMLDivElement>(null);

    const updateFilters = (value: ActiveFilter[]): void => {
        setPage(0);
        setActiveFilters(value);
    };

    const where = getSearchWhereValue(search[0], locale, firstAncestor ? [firstAncestor] : []);

    const { data, error, isLoading, isError, isFetching } = useSearchBlockQuery({
        endpoint: `${cms.apiUrl ? cms.apiUrl : OPTMIZELY_GRAPHQL_BASE_URL}${cms.apiKey}`,
        fetchParams: DEFAULT_FETCH_PARAMS,
    }, {
        locale: [...(locale ? [locale]: []), 'NEUTRAL'] as Locales[],
        where,
        orderBy: { _ranking: Ranking.Relevance, Changed: OrderBy.Desc },
        ancestors,
        ancestorLimit: ancestorLimit || 200,
        ...restParams,
        skip: page * itemsPerPage,
        limit: itemsPerPage,
    }, {
        enabled: !!search[0],
        queryKey: ['search-block'],
        keepPreviousData: true,
        staleTime: 1000 * 60 * 5,
        select: useCallback((result: SearchBlockQuery) => {
            return resolveSearchBlock(result, { tagsLabel, ancestorsTagLabel });
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []), // https://tkdodo.eu/blog/react-query-data-transformations
    });

    useEffect(() => {
        window.history.replaceState({}, '', getQueryURL(activeFilters, filters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilters]);

    useEffect(() => {
        if (data && filters) {
            const activeTabFilterLabels = getAllActiveFilterValueLabels(filters, activeFilters, ['search']);
            const searchAnalytics = {
                search: {
                    SearchResultsTerm: getActiveFilterSingleValue(activeFilters, 'search'),
                    SearchResultsNumber: data?.total || 0,
                    SearchFilter: activeTabFilterLabels.join(', '),
                },
            };
            window.digitalData = {
                ...(window.digitalData ? window.digitalData : {}),
                ...searchAnalytics,
            };
            window.dispatchEvent(new CustomEvent('analytics.search.submit'));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, filters]);

    useEffect(() => {
        if (scrollToEl?.current) {
            scrollToEl.current.scrollIntoView({ behavior: 'smooth' });
        }

        window.history.replaceState({}, '', getPageQueryURL(page));
    }, [page]);

    if (!!search[0] && isError) {
        console.error(error);

        return (
            <ListingError errorMessage={label.errorMessage} />
        );
    }

    const searchFilter = filters?.find(({ field }) => {
        return field === 'search';
    });

    const tabFilter = filters?.find(({ field }) => {
        return field === 'contenttype';
    });

    const tabFilterAncestors = filters?.find(({ field }) => {
        return field === 'ancestors';
    });

    const contenTypeActiveFilter = activeFilters.find(({ field }) => {
        return field === 'contenttype';
    });

    const ancestortActiveFilter = activeFilters.find(({ field }) => {
        return field === 'ancestors';
    });

    const inputValue = getActiveFilterSingleValue(activeFilters, 'search');
    const inputSuffixIcon = inputValue ? 'close' : undefined;
    const forValueSizeClass = inputValue.length > 30 ? 'long-text' : '';

    const showSidebarFilters = !!tabFilter?.items?.length || !!tabFilterAncestors?.items?.length;

    return (
        <>
            <div className="search-block-header">
                <div className="container search-block-header-container">
                    <div className="search-block-for-column">
                        <p className="search-block-for-label">{inputValue.trim() ? label.withValueSearchForLabel : label.noValueSearchForLabel}</p>
                        {!!inputValue.trim() && (
                            <p className={`search-block-for-value ${forValueSizeClass}`}>&lsquo;{inputValue}&rsquo;</p>
                        )}
                    </div>
                    <div className="search-block-input-column">
                        {searchFilter && (
                            <ListingFilterInput
                                placeholder={searchFilter.placeholder}
                                field={searchFilter.field}
                                activeFilters={activeFilters}
                                setActiveFilters={updateFilters}
                                prefixIcon="search"
                                suffixIcon={inputSuffixIcon}
                                onSuffixIconClick={(): void => {
                                    updateFilters(getUpdatedFilters(activeFilters, searchFilter.field, ['']));
                                }}
                                maxLength={50}
                            />
                        )}
                    </div>
                </div>
            </div>
            {isLoading && !!search[0] && (
                <div className="listing-app" ref={scrollToEl}>
                    <div className="container">
                        <ListingLoader loadingMessage={label.loadingMessage} />
                    </div>
                </div>
            )}
            {!!inputValue.trim() && data && !isLoading && (
                <div className="listing-app" ref={scrollToEl}>
                    <div className="container">
                        <div className="search-block-listing-app-container">
                            {showSidebarFilters && (
                                <div className="search-block-sidebar">
                                    {tabFilter && (
                                        <RadioTab fullWidth={true} cols="1-lg">
                                            {tabFilter.items?.length && (
                                                <>
                                                    {tabFilter.items.map((props, i) => {
                                                        const key = `${props.label}-${i}`;
                                                        const suffixText = getTabSearchCount(props.value, data.searchCounts) || '0';

                                                        const labelToRender = removeBrandPrefixFromLabel(props.label);

                                                        return (
                                                            <RadioTabItem
                                                                disabled={props.value !== '' && Number(suffixText) === 0}
                                                                key={key}
                                                                icon={getTabIconFromValue(props.value)}
                                                                suffixText={suffixText}
                                                                label={labelToRender}
                                                                id={`${props.label}-${i}`}
                                                                name={tabFilter.field}
                                                                value={props.value}
                                                                checked={props.value === (contenTypeActiveFilter?.values[0] || '')}
                                                                onChange={(e): void => {
                                                                    updateFilters(getUpdatedFilters(activeFilters, tabFilter.field, e.target.value ? [e.target.value] : []));
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                    {tabFilter.items.length % 2 === 1 && (
                                                        <div className="radio-tab-item radio-tab-item-empty"><span className="input-label" /></div>
                                                    )}
                                                </>
                                            )}
                                        </RadioTab>
                                    )}
                                    {tabFilterAncestors && (
                                        <RadioTab fullWidth={true} cols="1-lg">
                                            {tabFilterAncestors.items?.length && (
                                                <>
                                                    {tabFilterAncestors.items.map((props, i) => {
                                                        const key = `${props.label}-${i}`;
                                                        const suffixText = props.value === ''
                                                            ? getTabSearchCount(props.value, data.searchCounts) || '0'
                                                            : getAncestorTabSearchCount(props.value, data.ancestorCounts, firstAncestor) || '0';

                                                        const labelToRender = removeBrandPrefixFromLabel(props.label);

                                                        return (
                                                            <RadioTabItem
                                                                disabled={props.value !== '' && Number(suffixText) === 0}
                                                                key={key}
                                                                icon={props.icon ?? 'menu'}
                                                                suffixText={suffixText}
                                                                label={labelToRender}
                                                                id={`${props.label}-${i}`}
                                                                name={tabFilterAncestors.field}
                                                                value={props.value}
                                                                checked={props.value === (ancestortActiveFilter?.values[0] || '')}
                                                                onChange={(e): void => {
                                                                    updateFilters(getUpdatedFilters(activeFilters, tabFilterAncestors.field, e.target.value ? [e.target.value] : []));
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                    {tabFilterAncestors.items.length % 2 === 1 && (
                                                        <div className="radio-tab-item radio-tab-item-empty"><span className="input-label" /></div>
                                                    )}
                                                </>
                                            )}
                                        </RadioTab>
                                    )}
                                </div>
                            )}
                            <ListingContent
                                isFetching={isFetching}
                                isNoItems={!data.items.length}
                                pagination={{
                                    itemsPerPage,
                                    page,
                                    setPage,
                                    totalItems: data.total,
                                }}
                                loadingLabel={label.loadingMessage}
                                noItemsLabel={label.noItemMessage}
                                cols="1"
                            >
                                <div className="search-block-search-result">
                                    {data.items.map((props, i) => {
                                        const key=`${props.title}-${i}`;
                                        return (
                                            <SearchItem
                                                key={key}
                                                {...props}
                                                onClick={(): void => {
                                                    const searchAnalytics = {
                                                        search: {
                                                            ...(window?.digitalData?.search ? window.digitalData.search : {}),
                                                            SearchResultClicked: props.title,
                                                            SearchResultPosition: i + 1,
                                                        },
                                                    };

                                                    window.digitalData = {
                                                        ...(window.digitalData ? window.digitalData : {}),
                                                        ...searchAnalytics,
                                                    };
                                                    window.dispatchEvent(new CustomEvent('analytics.search.click'));
                                                }}
                                            />
                                        );
                                    })}
                                </div>
                            </ListingContent>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const queryClient = new QueryClient();

interface SearchBlockInitProps {
    el: HTMLDivElement;
    props: SearchBlockProps;
}

const SearchBlockInit = ({ el, props }: SearchBlockInitProps): void => {
    const root = ReactDOMClient.createRoot(el);

    root.render(
        <QueryClientProvider client={queryClient}>
            <SearchBlockProvider {...props}>
                <SearchSection />
            </SearchBlockProvider>
        </QueryClientProvider>
    );
};

export default SearchBlockInit;
